import { useTranslation } from '@jq4race/i18n';
import type { LocationResponse } from '@jq4race/shared';
import {
  Badge,
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
  Skeleton,
  Table,
  TableBody,
  TableCell,
  TableEmpty,
  TableHead,
  TableHeader,
  TableRow,
} from '@jq4race/ui';
import { Link } from '@tanstack/react-router';
import type { LucideIcon } from 'lucide-react';
import {
  Coins,
  Gift,
  LayersIcon,
  LayoutTemplate,
  ListChecks,
  MapPin,
  MoreHorizontal,
  Pencil,
  Power,
  PowerOff,
  ReceiptText,
  Tablet,
  Ticket,
  Users,
} from 'lucide-react';
import { enumLabel } from '../../../../shared/i18n/enum-label';
import { STATUS_TONE } from '../constants';

const rowActionClass =
  'inline-flex h-8 w-8 items-center justify-center rounded text-jq-graphite transition hover:bg-jq-surface-2 hover:text-jq-navy focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring/60';

// ---------------------------------------------------------------------------
// Menu action config — add future config pages here (one entry = one menu item)
// `labelKey` maps to `locations:table.menuItems.<labelKey>` in the translation
// catalog — it is a translation key, not display text.
// ---------------------------------------------------------------------------
export interface MenuAction {
  to: string;
  labelKey: string;
  Icon: LucideIcon;
}

export const MENU_ACTIONS: MenuAction[] = [
  { to: '/dashboard/locations/$locationId/zones', labelKey: 'zones', Icon: LayersIcon },
  { to: '/dashboard/locations/$locationId/assignments', labelKey: 'assignments', Icon: Users },
  { to: '/dashboard/locations/$locationId/tariff', labelKey: 'tariff', Icon: ReceiptText },
  {
    to: '/dashboard/locations/$locationId/niche-config',
    labelKey: 'nicheTemplate',
    Icon: LayoutTemplate,
  },
  {
    to: '/dashboard/locations/$locationId/checkin-config',
    labelKey: 'checkinFields',
    Icon: ListChecks,
  },
  {
    to: '/dashboard/locations/$locationId/ticket-types-config',
    labelKey: 'ticketTypes',
    Icon: Ticket,
  },
  {
    to: '/dashboard/locations/$locationId/tip-config',
    labelKey: 'tipConfiguration',
    Icon: Coins,
  },
  {
    to: '/dashboard/locations/$locationId/comp-config',
    labelKey: 'comps',
    Icon: Gift,
  },
  {
    to: '/dashboard/locations/$locationId/station-devices',
    labelKey: 'stationDevices',
    Icon: Tablet,
  },
];

interface LocationsTableProps {
  locations: LocationResponse[];
  loading: boolean;
  onDeactivate: (location: LocationResponse) => void;
  onActivate: (location: LocationResponse) => void;
}

export function LocationsTable({
  locations,
  loading,
  onDeactivate,
  onActivate,
}: LocationsTableProps) {
  const { t } = useTranslation('locations');

  return (
    <Table>
      <TableHeader>
        <TableRow>
          <TableHead>{t('table.headers.name')}</TableHead>
          <TableHead>{t('table.headers.niche')}</TableHead>
          <TableHead>{t('table.headers.status')}</TableHead>
          <TableHead>{t('table.headers.timezone')}</TableHead>
          <TableHead>{t('table.headers.currency')}</TableHead>
          <TableHead className="w-[120px] text-right">{t('table.headers.actions')}</TableHead>
        </TableRow>
      </TableHeader>
      <TableBody>
        {loading && locations.length === 0 ? (
          <SkeletonRows />
        ) : locations.length === 0 ? (
          <TableEmpty colSpan={6}>
            <EmptyState />
          </TableEmpty>
        ) : (
          locations.map((loc) => (
            <TableRow key={loc.id}>
              <TableCell>
                <span className="font-medium text-jq-charcoal">{loc.name}</span>
              </TableCell>
              <TableCell>{enumLabel(t, 'niche', loc.niche)}</TableCell>
              <TableCell>
                <Badge variant={STATUS_TONE[loc.status]} className="capitalize">
                  {t(`status.${loc.status}`)}
                </Badge>
              </TableCell>
              <TableCell className="text-jq-graphite">{loc.timezone}</TableCell>
              <TableCell className="text-jq-graphite">{loc.currency}</TableCell>
              <TableCell className="text-right">
                <div className="flex items-center justify-end gap-1">
                  {/* Inline primary: Edit */}
                  <Link
                    to="/dashboard/locations/$locationId/edit"
                    params={{ locationId: loc.id }}
                    className={rowActionClass}
                    aria-label={t('table.rowActions.edit', { name: loc.name })}
                  >
                    <Pencil className="h-3.5 w-3.5" strokeWidth={1.75} />
                  </Link>

                  {/* Inline primary: Activate / Deactivate (conditional) */}
                  {loc.status === 'active' ? (
                    <button
                      type="button"
                      onClick={() => onDeactivate(loc)}
                      className={rowActionClass}
                      aria-label={t('table.rowActions.deactivate', { name: loc.name })}
                    >
                      <PowerOff className="h-3.5 w-3.5" strokeWidth={1.75} />
                    </button>
                  ) : (
                    <button
                      type="button"
                      onClick={() => onActivate(loc)}
                      className={rowActionClass}
                      aria-label={t('table.rowActions.activate', { name: loc.name })}
                    >
                      <Power className="h-3.5 w-3.5" strokeWidth={1.75} />
                    </button>
                  )}

                  {/* Overflow menu: secondary navigation actions */}
                  <DropdownMenu>
                    <DropdownMenuTrigger
                      className={rowActionClass}
                      aria-label={t('table.rowActions.actionsFor', { name: loc.name })}
                    >
                      <MoreHorizontal className="h-3.5 w-3.5" strokeWidth={1.75} />
                    </DropdownMenuTrigger>
                    <DropdownMenuContent align="end">
                      <DropdownMenuLabel>{t('table.menu.configuration')}</DropdownMenuLabel>
                      <DropdownMenuSeparator />
                      {MENU_ACTIONS.map(({ to, labelKey, Icon }) => {
                        const label = t(`table.menuItems.${labelKey}`);
                        return (
                          <DropdownMenuItem key={to} asChild>
                            <Link
                              to={to}
                              params={{ locationId: loc.id }}
                              aria-label={t('table.menu.itemFor', { label, name: loc.name })}
                            >
                              <Icon className="h-3.5 w-3.5" strokeWidth={1.75} />
                              {label}
                            </Link>
                          </DropdownMenuItem>
                        );
                      })}
                    </DropdownMenuContent>
                  </DropdownMenu>
                </div>
              </TableCell>
            </TableRow>
          ))
        )}
      </TableBody>
    </Table>
  );
}

function SkeletonRows() {
  return (
    <>
      {Array.from({ length: 3 }).map((_, i) => (
        // biome-ignore lint/suspicious/noArrayIndexKey: skeletons are positional
        <TableRow key={`skeleton-${i}`}>
          {Array.from({ length: 6 }).map((_, j) => (
            // biome-ignore lint/suspicious/noArrayIndexKey: skeletons are positional
            <TableCell key={`cell-${j}`}>
              <Skeleton className="h-4 w-full" />
            </TableCell>
          ))}
        </TableRow>
      ))}
    </>
  );
}

function EmptyState() {
  const { t } = useTranslation('locations');
  return (
    <div className="flex flex-col items-center justify-center gap-3 py-12 text-center">
      <MapPin className="h-10 w-10 text-jq-stone" strokeWidth={1.25} />
      <p className="text-[14px] font-medium text-jq-charcoal">{t('table.empty.title')}</p>
      <p className="max-w-sm text-[13px] text-jq-graphite">{t('table.empty.description')}</p>
    </div>
  );
}
